import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { adminHeaders, api } from "../../lib/api";
import { copyRawJson, prettyRawJson, rewardStatusLabel, rewardTypeLabel } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";

interface RewardRow {
  id: number;
  member_account: string;
  reward_type: string;
  reward_status: string;
  amount: number;
  created_at: string;
  claimed_at?: string | null;
  is_test: number;
  source_deposit_id?: number | null;
  unique_key?: string | null;
  invite_manual_review_id?: number | null;
  source_inviter_account?: string | null;
  source_invitee_account?: string | null;
  source_invitee_line_nickname?: string | null;
  source_first_deposit_amount?: number | null;
  source_second_deposit_amount?: number | null;
  source_third_deposit_amount?: number | null;
  crawler_dispatch_task_id?: number | null;
  crawler_dispatch_mode?: string | null;
  crawler_dispatch_status?: string | null;
  crawler_dispatch_method?: string | null;
  crawler_dispatch_scheduled_at?: string | null;
  crawler_dispatch_queued_at?: string | null;
  crawler_dispatch_started_at?: string | null;
  crawler_dispatch_completed_at?: string | null;
  crawler_dispatch_failed_at?: string | null;
  crawler_dispatch_failed_reason?: string | null;
  crawler_dispatch_attempt_count?: number | null;
  crawler_dispatch_last_attempt_at?: string | null;
  crawler_reference_id?: string | null;
}

type RewardAction = "completed" | "failed" | "canceled";

interface PendingRewardAction {
  row: RewardRow;
  action: RewardAction;
}

interface PendingCrawlerDispatch {
  row: RewardRow;
}

const PROCESSING_TIMEOUT_MINUTES = 30;
const MAX_ATTEMPT_COUNT = 3;

const tabs = [
  ["all", "全部"],
  ["ready", "待派發"],
  ["completed", "已完成"],
  ["failed", "失敗 / 待處理"],
  ["canceled", "已取消"]
] as const;

const rewardTypeOptions = [
  "",
  "newbie_step1_70",
  "newbie_step2_10",
  "newbie_step3_second_deposit_80",
  "newbie_step4_quiz_10",
  "newbie_step5_third_deposit_50",
  "newbie_step6_red_packet_draw",
  "newbie_step6_red_packet",
  "newbie_step7_final_10",
  "newbie_step7_graduation_gift",
  "newbie_bonus_red_packet_draw",
  "graduate_launch_red_packet_draw",
  "advanced_launch_red_packet_draw",
  "weekly_bright_red_packet_draw",
  "weekend_bright_red_packet_draw",
  "weekend_advanced_red_packet_draw",
  "regular_deposit_rebate",
  "invite_join_group_6666",
  "invite_first_deposit_50000",
  "invite_second_stage_30000",
  "invite_third_deposit_10000",
  "group_checkin_bonus",
  "gift_helper_bonus",
  "birthday_bonus_manual",
  "rescue_fund_monthly",
  "manual_compensation",
  "manual_clawback"
] as const;

const rewardStatusOptions = [
  ["", "全部狀態"],
  ["ready", "待派發"],
  ["completed", "已完成"],
  ["failed", "失敗 / 待處理"],
  ["canceled", "已取消"]
] as const;

const systemPreparedTypes = new Set([
  "newbie_step1_70",
  "newbie_step2_10",
  "newbie_step3_second_deposit_80",
  "newbie_step4_quiz_10",
  "newbie_step5_third_deposit_50",
  "newbie_step6_red_packet_draw",
  "newbie_step6_red_packet",
  "newbie_step7_final_10",
  "newbie_step7_graduation_gift",
  "newbie_bonus_red_packet_draw",
  "graduate_launch_red_packet_draw",
  "advanced_launch_red_packet_draw",
  "weekly_bright_red_packet_draw",
  "weekend_bright_red_packet_draw",
  "weekend_advanced_red_packet_draw",
  "regular_deposit_rebate"
]);

const inviteRewardTypes = new Set([
  "invite_join_group_6666",
  "invite_first_deposit_50000",
  "invite_second_stage_30000",
  "invite_third_deposit_10000"
]);

const manualConfirmTypes = new Set([
  "birthday_bonus_manual",
  "rescue_bonus_manual",
  "rescue_fund_monthly",
  "manual_compensation",
  "manual_clawback",
  "group_checkin_bonus",
  "gift_helper_bonus"
]);

const crawlerDispatchableTypes = new Set([
  "newbie_step1_70",
  "newbie_step2_10",
  "newbie_step3_second_deposit_80",
  "newbie_step4_quiz_10",
  "newbie_step5_third_deposit_50",
  "newbie_step6_red_packet_draw",
  "newbie_step7_final_10",
  "newbie_step7_graduation_gift",
  "newbie_bonus_red_packet_draw",
  "graduate_launch_red_packet_draw",
  "advanced_launch_red_packet_draw",
  "weekly_bright_red_packet_draw",
  "weekend_bright_red_packet_draw",
  "weekend_advanced_red_packet_draw",
  "regular_deposit_rebate",
  "invite_join_group_6666",
  "invite_first_deposit_50000",
  "invite_second_stage_30000",
  "invite_third_deposit_10000",
  "gift_helper_bonus",
  "birthday_bonus_manual",
  "rescue_fund_monthly",
  "manual_compensation"
]);

const newbieRewardOrder = new Map([
  ["newbie_step1_70", 1],
  ["newbie_step2_10", 2],
  ["newbie_step3_second_deposit_80", 3],
  ["newbie_step4_quiz_10", 4],
  ["newbie_step5_third_deposit_50", 5],
  ["newbie_step6_red_packet_draw", 6],
  ["newbie_step7_final_10", 7],
  ["newbie_step7_graduation_gift", 8]
]);

export function AdminRewardsPage() {
  const isLocalDev = isLocalDevFrontend();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState<RewardRow[]>([]);
  const [memberFilter, setMemberFilter] = useState(params.get("member_account") ?? params.get("account") ?? "");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingRewardAction | null>(null);
  const [pendingCrawlerDispatch, setPendingCrawlerDispatch] = useState<PendingCrawlerDispatch | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [crawlerUpdatingId, setCrawlerUpdatingId] = useState<number | null>(null);

  async function load() {
    setError("");
    if (!localStorage.getItem("admin_token") && !localStorage.getItem("adminToken")) {
      setRows([]);
      setError(isLocalDev ? "Admin Token 尚未設定，local/dev 可使用 jy-admin-test-token。" : "Admin Token 尚未設定，請輸入正式後台 token。");
      return;
    }

    try {
      const query = status === "all" ? "" : `?status=${status}`;
      setRows(await api<RewardRow[]>(`/api/admin/rewards${query}`, { headers: adminHeaders() }));
    } catch (err) {
      const nextMessage = err instanceof Error ? err.message : "讀取派發資料失敗。";
      setRows([]);
      setError(nextMessage === "Unauthorized" ? (isLocalDev ? "Admin Token 錯誤，local/dev 可使用 jy-admin-test-token。" : "Admin Token 錯誤，請確認正式 token。") : nextMessage);
    }
  }

  async function updateReward(row: RewardRow, rewardStatus: RewardAction, note = "") {
    setError("");
    setMessage("");
    setUpdatingId(row.id);
    try {
      if (isLocalDev && row.crawler_dispatch_task_id && (rewardStatus === "completed" || rewardStatus === "failed")) {
        const simulateEndpoint = rewardStatus === "completed" ? "simulate-completed" : "simulate-failed";
        await api(`/api/admin/crawler/reward-dispatch-tasks/${row.crawler_dispatch_task_id}/${simulateEndpoint}`, {
          method: "POST",
          headers: adminHeaders(),
          body: JSON.stringify(rewardStatus === "completed"
            ? { worker_id: "local-dev-rewards-page", note: "local/dev rewards page simulated completed" }
            : { worker_id: "local-dev-rewards-page", note: "local/dev rewards page simulated failed", failed_reason: "local/dev simulated failed" })
        });
        setMessage(rewardStatus === "completed" ? "local/dev 已模擬贈禮小幫手派發完成。" : "local/dev 已模擬贈禮小幫手派發失敗。");
        setPendingAction(null);
        await load();
        return;
      }
      const action = rewardStatus === "completed" ? "complete" : rewardStatus === "failed" ? "fail" : "cancel";
      await api(`/api/admin/rewards/${row.id}/${action}`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(rewardStatus === "completed" ? { confirm_completed: true, admin_note: note } : {})
      });
      setMessage(rewardSuccessMessage(rewardStatus, isLocalDev));
      setPendingAction(null);
      await load();
    } catch (err) {
      const nextMessage = err instanceof Error ? err.message : "更新派發狀態失敗。";
      setError(nextMessage === "Unauthorized" ? (isLocalDev ? "Admin Token 錯誤，local/dev 可使用 jy-admin-test-token。" : "Admin Token 錯誤，請確認正式 token。") : nextMessage);
    } finally {
      setUpdatingId(null);
    }
  }

  async function enqueueCrawlerDispatch(row: RewardRow) {
    setError("");
    setMessage("");
    setCrawlerUpdatingId(row.id);
    try {
      const result = await api<{ created: boolean; task_id: number | null; dispatch_status: string | null; dispatch_mode: string | null }>(
        `/api/admin/rewards/${row.id}/enqueue-crawler-dispatch`,
        {
          method: "POST",
          headers: adminHeaders(),
          body: JSON.stringify({})
        }
      );
      setMessage(result.created ? "已排入贈禮小幫手派發任務。" : `此獎勵已存在贈禮小幫手任務 #${result.task_id}。`);
      setPendingCrawlerDispatch(null);
      await load();
    } catch (err) {
      const nextMessage = err instanceof Error ? err.message : "排入贈禮小幫手失敗。";
      setError(nextMessage === "Unauthorized" ? (isLocalDev ? "Admin Token 錯誤，local/dev 可使用 jy-admin-test-token。" : "Admin Token 錯誤，請確認正式 token。") : nextMessage);
    } finally {
      setCrawlerUpdatingId(null);
    }
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : "讀取派發資料失敗。"));
  }, [status]);

  const filteredRows = filterRows(rows, { memberFilter, typeFilter, statusFilter, dateFrom, dateTo });
  const displayRows = sortRewardRowsForDisplay(filteredRows, memberFilter);
  const birthdayRewardRows = filteredRows.filter((row) => row.reward_type.includes("birthday") && row.reward_type !== "birthday_bonus_manual");

  return (
    <AdminLayout title="派發中心">
      <section className="admin-card">
        <div className="admin-card-head">
          <span>派發中心</span>
          <h2>獎勵派發總表</h2>
        </div>
        <p className="admin-muted">
          派發中心顯示所有 reward 的狀態。建單方式只代表獎勵如何建立；實際是否已排入贈禮小幫手，請看「贈禮小幫手派發」欄位。
        </p>
        <div className="admin-dispatch-legend">
          <span>待派發：尚未完成派發</span>
          <span>已完成：已確認派發成功</span>
          <span>失敗：需人工追蹤</span>
          <span>已取消：不再派發</span>
        </div>
        {birthdayRewardRows.length ? (
          <p className="error-box">
            注意：目前查詢結果中有 {birthdayRewardRows.length} 筆非 `birthday_bonus_manual` 的生日類獎勵，請確認是否為舊資料。
          </p>
        ) : null}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>查詢</span>
          <h2>派發資料篩選</h2>
        </div>
        <div className="tabs admin-status-tabs">
          {tabs.map(([key, label]) => (
            <button className={status === key ? "active" : ""} onClick={() => setStatus(key)} key={key}>
              {label}
            </button>
          ))}
        </div>
        <AdminFilterBar>
          <label>
            會員帳號
            <input value={memberFilter} onChange={(event) => setMemberFilter(event.target.value)} placeholder={isLocalDev ? "test031" : "輸入會員帳號"} />
          </label>
          <label>
            獎勵類型
            <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
              {rewardTypeOptions.map((value) => (
                <option key={value || "all"} value={value}>{value ? rewardTypeLabel(value) : "全部類型"}</option>
              ))}
            </select>
          </label>
          <label>
            獎勵狀態
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              {rewardStatusOptions.map(([value, label]) => (
                <option key={value || "all"} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            建立日期起
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            建立日期迄
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <button className="admin-btn admin-btn-primary" onClick={load}>重新整理</button>
          <button className="admin-btn admin-btn-secondary" onClick={() => { setMemberFilter(""); setTypeFilter(""); setStatusFilter(""); setDateFrom(""); setDateTo(""); }}>
            清除條件
          </button>
        </AdminFilterBar>
      </section>

      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-card">
        <div className="admin-card-head">
          <span>派發清單</span>
          <h2>獎勵派發清單</h2>
        </div>
        <div className="admin-active-filters">
          <span>狀態：{tabLabel(status)}</span>
          <span>會員：{memberFilter.trim() || "全部"}</span>
          <span>類型：{typeFilter ? rewardTypeLabel(typeFilter) : "全部"}</span>
          <span>獎勵狀態：{statusFilter ? rewardStatusLabel(statusFilter) : "全部"}</span>
          <span>日期：{dateFrom || "不限"} ~ {dateTo || "不限"}</span>
          <span>筆數：{displayRows.length}</span>
        </div>
        <p className="admin-muted">
          建單方式只代表這筆獎勵如何建立；實際是否已排入贈禮小幫手，請以「贈禮小幫手派發」欄位的任務 ID、狀態與模式為準。
        </p>
        <RewardCardList
          rows={displayRows}
          onRequestAction={(row, action) => {
            setError("");
            setMessage("");
            setPendingAction({ row, action });
          }}
          error={error}
          isLocalDev={isLocalDev}
          updatingId={updatingId}
          crawlerUpdatingId={crawlerUpdatingId}
          onRequestCrawlerDispatch={(row) => {
            setError("");
            setMessage("");
            setPendingCrawlerDispatch({ row });
          }}
        />
      </section>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction ? rewardConfirmCopy(pendingAction.action).title : ""}
        message={pendingAction ? rewardConfirmCopy(pendingAction.action).message : ""}
        confirmText={pendingAction ? rewardConfirmCopy(pendingAction.action).confirmText : "確認"}
        cancelText="取消"
        variant={pendingAction?.action === "completed" ? "normal" : "danger"}
        loading={Boolean(pendingAction && updatingId === pendingAction.row.id)}
        onCancel={() => {
          if (!updatingId) setPendingAction(null);
        }}
        onConfirm={() => {
          if (!pendingAction) return;
          updateReward(
            pendingAction.row,
            pendingAction.action,
            pendingAction.action === "completed" && isLocalDev ? "local/dev 確認派發完成" : ""
          );
        }}
      />
      <ConfirmDialog
        open={Boolean(pendingCrawlerDispatch)}
        title="確認排入贈禮小幫手？"
        message="系統會將此獎勵排入贈禮小幫手派發任務。獎勵本身仍會保持待派發，等爬蟲成功回寫後才完成。"
        confirmText="確認排入"
        cancelText="取消"
        variant="normal"
        loading={Boolean(pendingCrawlerDispatch && crawlerUpdatingId === pendingCrawlerDispatch.row.id)}
        onCancel={() => {
          if (!crawlerUpdatingId) setPendingCrawlerDispatch(null);
        }}
        onConfirm={() => {
          if (!pendingCrawlerDispatch) return;
          enqueueCrawlerDispatch(pendingCrawlerDispatch.row);
        }}
      />
    </AdminLayout>
  );
}

function rewardConfirmCopy(action: RewardAction) {
  if (action === "completed") {
    return {
      title: "確認已派發？",
      message: "確認後此派發單會標記為已完成，不能重複操作。請確認實際已完成派發。",
      confirmText: "確認已派發"
    };
  }
  if (action === "failed") {
    return {
      title: "確認標記失敗？",
      message: "此操作會將派發單標記為失敗，請確認原因無誤。",
      confirmText: "確認標記失敗"
    };
  }
  return {
    title: "確認取消派發？",
    message: "取消後此派發單將不再派發，請確認這是正確操作。",
    confirmText: "確認取消"
  };
}

function rewardSuccessMessage(action: RewardAction, isLocalDev: boolean) {
  if (action === "completed") return isLocalDev ? "local/dev 已確認派發完成。" : "派發單已完成。";
  if (action === "failed") return "已標記失敗。";
  return "已取消派發。";
}

function RewardCardList({
  rows,
  onRequestAction,
  onRequestCrawlerDispatch,
  error,
  isLocalDev,
  updatingId,
  crawlerUpdatingId
}: {
  rows: RewardRow[];
  onRequestAction: (row: RewardRow, action: RewardAction) => void;
  onRequestCrawlerDispatch: (row: RewardRow) => void;
  error: string;
  isLocalDev: boolean;
  updatingId: number | null;
  crawlerUpdatingId: number | null;
}) {
  const [copyMessage, setCopyMessage] = useState("");

  async function copy(row: RewardRow) {
    await copyRawJson(row);
    setCopyMessage(`已複製獎勵 #${row.id} 的原始資料。`);
  }

  return (
    <>
      {copyMessage ? <p className="result">{copyMessage}</p> : null}
      <div className="reward-card-list">
        {rows.map((row) => {
          const taskStatus = getCrawlerDispatchCardStatus(row);
          const showCrawlerAction = shouldShowCrawlerDispatchAction(row);
          return (
            <article className="reward-card" key={row.id}>
              <div className="reward-card-main">
                <div className="reward-card-title">
                  <div>
                    <span className="reward-card-eyebrow">獎勵 #{row.id}</span>
                    <h3>{rewardTypeLabel(row.reward_type)}</h3>
                  </div>
                  <strong>{money(row.amount)}</strong>
                </div>

                <div className="reward-card-badges">
                  <span className={rewardStatusBadgeClass(row.reward_status)}>{rewardStatusLabel(row.reward_status)}</span>
                  <span className="status-pill live">{dispatchMethodLabel(row.reward_type)}</span>
                  {taskStatus ? <span className={taskStatus.className}>{taskStatus.label}</span> : <span className="status-pill muted">尚未排入贈禮小幫手</span>}
                  {row.is_test ? <span className="status-pill soon">測試資料</span> : null}
                </div>

                <div className="reward-card-summary-grid">
                  <SummaryItem label="會員帳號" value={row.member_account} />
                  <SummaryItem label="建立時間" value={displayDate(row.created_at)} />
                  <SummaryItem label="完成時間" value={displayDate(row.claimed_at)} />
                  <SummaryItem label="任務 ID" value={row.crawler_dispatch_task_id ? `#${row.crawler_dispatch_task_id}` : "-"} />
                  <SummaryItem label="派發模式" value={row.crawler_dispatch_mode ? crawlerDispatchModeLabel(row.crawler_dispatch_mode) : "-"} />
                  <SummaryItem label="來源資料" value={sourceSummaryText(row)} />
                </div>

                <p className={rewardNextStepClass(row)}>{rewardNextStepText(row)}</p>
              </div>

              <div className="reward-card-actions">
                {showCrawlerAction ? (
                  <button
                    className="admin-btn admin-btn-action"
                    onClick={() => onRequestCrawlerDispatch(row)}
                    disabled={crawlerUpdatingId === row.id}
                  >
                    {crawlerUpdatingId === row.id ? "處理中..." : "排入贈禮小幫手"}
                  </button>
                ) : null}
                {renderRewardActions(row, onRequestAction, isLocalDev, updatingId)}
              </div>

              <details className="reward-card-details">
                <summary>展開明細</summary>
                <div className="reward-detail-grid">
                  <DetailItem label="獎勵 ID" value={`#${row.id}`} />
                  <DetailItem label="會員帳號" value={row.member_account} />
                  <DetailItem label="獎勵類型" value={rewardTypeLabel(row.reward_type)} />
                  <DetailItem label="金額" value={money(row.amount)} />
                  <DetailItem label="獎勵狀態" value={rewardStatusLabel(row.reward_status)} />
                  <DetailItem label="建單方式" value={dispatchMethodLabel(row.reward_type)} />
                  <DetailItem label="建立時間" value={displayDate(row.created_at)} />
                  <DetailItem label="完成時間" value={displayDate(row.claimed_at)} />
                  <DetailItem label="測試資料" value={row.is_test ? "是" : "否"} />
                  <DetailItem label="防重複資料" value={row.unique_key ? "已記錄" : "-"} />
                  <DetailItem label="存款 ID" value={row.source_deposit_id ? `#${row.source_deposit_id}` : "-"} />
                  <DetailItem label="快審 ID" value={row.invite_manual_review_id ? `#${row.invite_manual_review_id}` : "-"} />
                  <DetailItem label="來源好友" value={row.source_invitee_account ?? inviteeFromUniqueKey(row.unique_key) ?? "-"} />
                  <DetailItem label="好友 LINE" value={row.source_invitee_line_nickname} />
                  <DetailItem label="首存金額" value={formatOptionalMoney(row.source_first_deposit_amount)} />
                  <DetailItem label="二存金額" value={formatOptionalMoney(row.source_second_deposit_amount)} />
                  <DetailItem label="三存金額" value={formatOptionalMoney(row.source_third_deposit_amount)} />
                  <DetailItem label="任務 ID" value={row.crawler_dispatch_task_id ? `#${row.crawler_dispatch_task_id}` : "-"} />
                  <DetailItem label="任務狀態" value={crawlerDispatchDetailStatusLabel(row)} />
                  <DetailItem label="派發模式" value={row.crawler_dispatch_mode ? crawlerDispatchModeLabel(row.crawler_dispatch_mode) : "-"} />
                  <DetailItem label="預計派發時間" value={displayDate(row.crawler_dispatch_scheduled_at)} />
                  <DetailItem label="排入時間" value={displayDate(row.crawler_dispatch_queued_at)} />
                  <DetailItem label="開始處理時間" value={displayDate(row.crawler_dispatch_started_at)} />
                  <DetailItem label="最近嘗試時間" value={displayDate(row.crawler_dispatch_last_attempt_at)} />
                  <DetailItem label="派發完成時間" value={displayDate(row.crawler_dispatch_completed_at)} />
                  <DetailItem label="派發失敗時間" value={displayDate(row.crawler_dispatch_failed_at)} />
                  <DetailItem label="嘗試次數" value={`${crawlerDispatchAttemptCount(row)} / ${MAX_ATTEMPT_COUNT}`} />
                  <DetailItem label="爬蟲回傳編號" value={row.crawler_reference_id} />
                  <DetailItem className="wide" label="失敗原因" value={row.crawler_dispatch_failed_reason} />
                  <div className="reward-card-source-detail">
                    <span>來源明細</span>
                    {renderRewardSourceDetail(row)}
                  </div>
                </div>
                <details className="raw-details reward-card-raw">
                  <summary>查看原始資料</summary>
                  <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
                  <pre>{prettyRawJson(row)}</pre>
                </details>
                <button className="admin-btn admin-btn-action" onClick={() => copy(row)}>複製原始資料</button>
              </details>
            </article>
          );
        })}
        {!rows.length && !error ? (
          <p className="reward-card-empty">查無符合條件的獎勵資料。</p>
        ) : null}
      </div>
    </>
  );
}

function SummaryItem({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="reward-summary-item">
      <span>{label}</span>
      <strong>{displayValue(value)}</strong>
    </div>
  );
}

function DetailItem({ label, value, className = "" }: { label: string; value: string | number | null | undefined; className?: string }) {
  return (
    <div className={`reward-detail-item ${className}`.trim()}>
      <span>{label}</span>
      <strong>{displayValue(value)}</strong>
    </div>
  );
}

function renderRewardActions(
  row: RewardRow,
  onRequestAction: (row: RewardRow, action: RewardAction) => void,
  isLocalDev: boolean,
  updatingId: number | null
) {
  if (row.reward_status === "completed") return <span className="status-pill success">已派發完成</span>;
  if (row.reward_status === "canceled" || row.reward_status === "cancelled") return <span className="status-pill locked">已取消</span>;
  if (row.reward_status === "failed" || row.reward_status === "rejected") return <span className="status-pill soon">失敗 / 待人工處理</span>;
  if (row.reward_status === "ready") {
    return (
      <div className="table-actions">
        {isLocalDev ? <span className="status-pill live">local/dev</span> : null}
        <button className="admin-btn admin-btn-primary" onClick={() => onRequestAction(row, "completed")} disabled={updatingId === row.id}>
          {updatingId === row.id ? "處理中..." : isLocalDev ? "local/dev 確認完成" : "確認已派發"}
        </button>
        <button className="admin-btn admin-btn-danger" onClick={() => onRequestAction(row, "failed")} disabled={updatingId === row.id}>
          {updatingId === row.id ? "處理中..." : "標記失敗"}
        </button>
        <button className="admin-btn admin-btn-danger" onClick={() => onRequestAction(row, "canceled")} disabled={updatingId === row.id}>
          {updatingId === row.id ? "處理中..." : "取消派發"}
        </button>
      </div>
    );
  }
  return <span className="admin-muted">無可用操作</span>;
}

function shouldShowCrawlerDispatchAction(row: RewardRow) {
  if (row.crawler_dispatch_task_id) return false;
  if (!crawlerDispatchableTypes.has(row.reward_type)) return false;
  if (row.reward_status !== "ready") return false;
  if (!Number.isFinite(Number(row.amount)) || Number(row.amount) <= 0) return false;
  return true;
}

function getCrawlerDispatchCardStatus(row: RewardRow) {
  if (!row.crawler_dispatch_task_id) return null;
  const status = row.crawler_dispatch_status ?? "pending";
  if (isCrawlerDispatchProcessingTimedOut(row)) return { label: "處理逾時", className: "status-pill danger" };
  if (crawlerDispatchHasReachedMaxAttempts(row) && status === "failed") return { label: "已達最大重試次數", className: "status-pill danger" };
  if (status === "pending") return { label: crawlerDispatchAvailabilityLabel(row) === "尚未到派發時間" ? "尚未到派發時間" : "等待派發", className: "status-pill soon" };
  if (status === "processing") return { label: "派發中", className: "status-pill live" };
  if (status === "completed") return { label: "贈禮小幫手已完成", className: "status-pill success" };
  if (status === "failed") return { label: "贈禮小幫手派發失敗", className: "status-pill danger" };
  if (status === "canceled") return { label: "贈禮小幫手已取消", className: "status-pill locked" };
  return { label: crawlerDispatchStatusLabel(status), className: "status-pill muted" };
}

function rewardStatusBadgeClass(status: string) {
  if (status === "completed") return "status-pill success";
  if (status === "ready") return "status-pill soon";
  if (status === "failed" || status === "rejected") return "status-pill danger";
  if (status === "canceled" || status === "cancelled") return "status-pill locked";
  return "status-pill muted";
}

function rewardNextStepClass(row: RewardRow) {
  const text = rewardNextStepText(row);
  return text.includes("失敗") || text.includes("逾時") || text.includes("尚未排入") ? "reward-next-step danger" : "reward-next-step";
}

function rewardNextStepText(row: RewardRow) {
  const rewardStatus = row.reward_status;
  if (rewardStatus === "completed") return "已派發完成，不需要再操作。";
  if (rewardStatus === "canceled" || rewardStatus === "cancelled") return "此獎勵已取消，不會再派發。";
  if (rewardStatus === "failed" || rewardStatus === "rejected") return "此獎勵已標記失敗，請查看明細與後續處理紀錄。";
  if (row.crawler_dispatch_task_id) {
    const status = row.crawler_dispatch_status ?? "pending";
    if (isCrawlerDispatchProcessingTimedOut(row)) return "處理逾時，請至贈禮小幫手派發任務查看外部爬蟲狀態後處理。";
    if (status === "pending") {
      if (crawlerDispatchAvailabilityLabel(row) === "尚未到派發時間") return "尚未到派發時間，系統不會提前交給贈禮小幫手。";
      return "已排入贈禮小幫手，等待派發。";
    }
    if (status === "processing") return "贈禮小幫手派發中，請等待回寫結果。";
    if (status === "completed") return "贈禮小幫手已派發完成。";
    if (status === "failed") return "贈禮小幫手派發失敗，請前往贈禮小幫手派發任務處理。";
    if (status === "canceled") return "贈禮小幫手任務已取消，可前往任務頁查看是否需重新排入。";
  }
  if (shouldShowCrawlerDispatchAction(row)) return "尚未排入贈禮小幫手，可依需要排入任務。";
  if (!crawlerDispatchableTypes.has(row.reward_type)) return "此獎勵類型目前不支援贈禮小幫手派發。";
  return "目前無可用操作。";
}

function crawlerDispatchDetailStatusLabel(row: RewardRow) {
  if (!row.crawler_dispatch_task_id) return "-";
  if (isCrawlerDispatchProcessingTimedOut(row)) return "處理逾時";
  return crawlerDispatchStatusLabel(row.crawler_dispatch_status ?? "pending");
}

function sourceSummaryText(row: RewardRow) {
  if (inviteRewardTypes.has(row.reward_type)) {
    return row.source_invitee_account ?? inviteeFromUniqueKey(row.unique_key) ?? "邀友來源";
  }
  if (row.source_deposit_id) return `存款 #${row.source_deposit_id}`;
  if (row.invite_manual_review_id) return `快審 #${row.invite_manual_review_id}`;
  return "-";
}

function renderRewardSourceDetail(row: RewardRow) {
  if (inviteRewardTypes.has(row.reward_type)) return renderInviteRewardSource(row);
  const items = [
    row.source_deposit_id ? `存款 ID：#${row.source_deposit_id}` : "",
    row.invite_manual_review_id ? `快審 ID：#${row.invite_manual_review_id}` : "",
    row.unique_key ? "防重複資料：已記錄" : ""
  ].filter(Boolean);
  if (!items.length) return <p className="admin-muted">-</p>;
  return (
    <div className="summary-list">
      {items.map((item) => <p key={item}>{item}</p>)}
    </div>
  );
}

function displayValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function displayDate(value?: string | null) {
  return value ? shortDate(value) : "-";
}

function formatOptionalMoney(value?: number | null) {
  if (value === null || value === undefined) return "-";
  return money(value);
}

function crawlerDispatchStatusLabel(status: string) {
  if (status === "pending") return "等待派發";
  if (status === "processing") return "派發中";
  if (status === "completed") return "已派發完成";
  if (status === "failed") return "派發失敗";
  if (status === "canceled") return "已取消";
  return status;
}

function crawlerDispatchModeLabel(mode: string) {
  if (mode === "immediate_crawler_dispatch") return "立即排入";
  if (mode === "next_day_12_crawler_dispatch") return "明日 12 點後排入";
  if (mode === "manual_confirm_then_crawler_dispatch") return "人工確認後排入";
  return mode;
}

function crawlerDispatchAvailabilityLabel(row: Pick<RewardRow, "crawler_dispatch_status" | "crawler_dispatch_mode" | "crawler_dispatch_scheduled_at">) {
  if (row.crawler_dispatch_status !== "pending") return "-";
  if (row.crawler_dispatch_mode === "next_day_12_crawler_dispatch" && row.crawler_dispatch_scheduled_at) {
    const scheduledAt = new Date(row.crawler_dispatch_scheduled_at).getTime();
    if (Number.isFinite(scheduledAt) && scheduledAt > Date.now()) return "尚未到派發時間";
  }
  return "可派發";
}

function crawlerDispatchAttemptCount(row: Pick<RewardRow, "crawler_dispatch_attempt_count">) {
  const count = Number(row.crawler_dispatch_attempt_count ?? 0);
  return Number.isFinite(count) ? count : 0;
}

function crawlerDispatchHasReachedMaxAttempts(row: Pick<RewardRow, "crawler_dispatch_attempt_count">) {
  return crawlerDispatchAttemptCount(row) >= MAX_ATTEMPT_COUNT;
}

function isCrawlerDispatchProcessingTimedOut(
  row: Pick<RewardRow, "crawler_dispatch_status" | "crawler_dispatch_last_attempt_at" | "crawler_dispatch_started_at">
) {
  if (row.crawler_dispatch_status !== "processing") return false;
  const basis = row.crawler_dispatch_last_attempt_at || row.crawler_dispatch_started_at;
  if (!basis) return false;
  const basisTime = new Date(basis).getTime();
  if (!Number.isFinite(basisTime)) return false;
  return basisTime <= Date.now() - PROCESSING_TIMEOUT_MINUTES * 60 * 1000;
}

function filterRows(rows: RewardRow[], filters: { memberFilter: string; typeFilter: string; statusFilter: string; dateFrom: string; dateTo: string }) {
  return rows.filter((row) => {
    const memberNeedle = filters.memberFilter.trim().toLowerCase();
    const accountMatch = !memberNeedle || [
      row.member_account,
      row.source_invitee_account,
      row.source_invitee_line_nickname
    ].some((value) => String(value ?? "").toLowerCase().includes(memberNeedle));
    const typeMatch = !filters.typeFilter || row.reward_type === filters.typeFilter;
    const statusMatch = !filters.statusFilter || row.reward_status === filters.statusFilter;
    const date = String(row.created_at ?? "").slice(0, 10);
    const fromMatch = !filters.dateFrom || date >= filters.dateFrom;
    const toMatch = !filters.dateTo || date <= filters.dateTo;
    return accountMatch && typeMatch && statusMatch && fromMatch && toMatch;
  });
}

function sortRewardRowsForDisplay(rows: RewardRow[], memberFilter: string) {
  if (!memberFilter.trim() || !rows.some((row) => newbieRewardOrder.has(row.reward_type))) return rows;

  return [...rows].sort((left, right) => {
    const leftOrder = newbieRewardOrder.get(left.reward_type);
    const rightOrder = newbieRewardOrder.get(right.reward_type);
    if (leftOrder && rightOrder && leftOrder !== rightOrder) return leftOrder - rightOrder;
    if (leftOrder && !rightOrder) return -1;
    if (!leftOrder && rightOrder) return 1;

    const leftCreated = new Date(left.created_at).getTime();
    const rightCreated = new Date(right.created_at).getTime();
    if (Number.isFinite(leftCreated) && Number.isFinite(rightCreated) && leftCreated !== rightCreated) return leftCreated - rightCreated;
    return left.id - right.id;
  });
}

function tabLabel(currentStatus: string) {
  return tabs.find(([key]) => key === currentStatus)?.[1] ?? currentStatus;
}

function renderInviteRewardSource(row: RewardRow) {
  if (!inviteRewardTypes.has(row.reward_type)) return "-";
  const sourceInvitee = row.source_invitee_account ?? inviteeFromUniqueKey(row.unique_key) ?? "-";
  return (
    <div className="summary-list">
      <p><strong>來源好友：</strong>{sourceInvitee}</p>
      <p><strong>LINE：</strong>{row.source_invitee_line_nickname || "-"}</p>
      <p><strong>快審 ID：</strong>{row.invite_manual_review_id ? `#${row.invite_manual_review_id}` : "-"}</p>
    </div>
  );
}

function inviteeFromUniqueKey(uniqueKey?: string | null) {
  if (!uniqueKey) return null;
  const markers = [
    "_invite_join_group_6666_",
    "_invite_first_deposit_50000_",
    "_invite_second_stage_30000_",
    "_invite_third_deposit_10000_"
  ];
  for (const marker of markers) {
    const index = uniqueKey.indexOf(marker);
    if (index >= 0) return uniqueKey.slice(index + marker.length) || null;
  }
  return null;
}

function dispatchMethodLabel(type: string) {
  if (inviteRewardTypes.has(type)) return "邀友獎勵 / 自動建單";
  if (manualConfirmTypes.has(type) || type.includes("birthday") || type.includes("rescue") || type.includes("manual")) return "人工確認建單";
  if (systemPreparedTypes.has(type)) return "系統建立 / 派發中心確認";
  return "一般建單";
}
